import { useCallback, useEffect, useState } from "react";

import { mirrorColor } from "@corca/tiptap";
import * as Sentry from "@sentry/nextjs";
import { useTheme } from "next-themes";

import { twColorToRgb } from "@/utils/tailwindColors";

import { LABEL_COLORS } from "./labelColors";

import type { GGBEvalStatus, GGBApi } from "./types";
import type { Color } from "@corca/data";

const NON_GRAPHICAL_TYPES = ["numeric", "multivariablefunction"];

export function useGeogebraCommandRunner(
  {
    api,
    expression,
    rangedValueExpression,
    colors,
  }: {
    api: GGBApi | null;
    expression: string;
    rangedValueExpression: string;
    colors?: (Color | undefined)[];
  },
  onUpdate?: (api: GGBApi) => void,
) {
  const [evalStatus, setEvalStatus] = useState<GGBEvalStatus>("init");
  const { theme, systemTheme } = useTheme();
  const currentTheme = theme === "system" ? systemTheme : theme;

  const clear = useCallback((aplt: GGBApi | null) => {
    if (!aplt) return;

    for (const name of aplt.getAllObjectNames()) {
      aplt.deleteObject(name);
    }
  }, []);

  useEffect(() => {
    if (!api) return;

    clear(api);

    if (!expression) {
      setEvalStatus("empty");
      return;
    }

    if (rangedValueExpression) {
      const constRes = api.evalCommand(rangedValueExpression);
      if (!constRes) {
        console.warn("Failed to evaluate ranged value expression:", rangedValueExpression);
        Sentry.captureMessage("Failed to evaluate ranged value expression", {
          level: "warning",
          extra: { expression: rangedValueExpression },
        });
        setEvalStatus("error");
        return;
      }
    }

    const expressions = expression.split("\n").filter((expr) => expr.trim());
    const expressionLabels: { expressionIndex: number; labels: string[] }[] = [];

    for (let i = 0; i < expressions.length; i++) {
      const expr = expressions[i];
      const trimmedExpr = expr.trim();

      if (trimmedExpr) {
        const res = api.evalCommandGetLabels(trimmedExpr);

        if (res) {
          const labels = res.split(",").filter((label) => label.trim());

          const validLabels = labels.filter((label) => {
            const exists = api.exists(label);
            const isDefined = api.isDefined(label);
            const isVisible = api.getVisible(label);
            const objectType = api.getObjectType(label);

            const isGraphical = !NON_GRAPHICAL_TYPES.includes(objectType);

            return exists && isDefined && isVisible && isGraphical;
          });

          if (validLabels.length > 0) {
            expressionLabels.push({ expressionIndex: i, labels: validLabels });
          }
        }
      }
    }

    if (expressionLabels.length === 0) {
      console.warn("Failed to evaluate any expressions:", expressions);
      Sentry.captureMessage("Failed to evaluate any expressions", {
        level: "warning",
        extra: { expressions },
      });
      setEvalStatus("error");
      return;
    }

    for (const { expressionIndex, labels } of expressionLabels) {
      const color =
        colors?.[expressionIndex] || LABEL_COLORS[expressionIndex % LABEL_COLORS.length];
      const themeColor = currentTheme === "dark" ? mirrorColor(color) : color;
      const rgb = twColorToRgb(themeColor);

      if (rgb) {
        for (const label of labels) {
          api.setColor(label, ...rgb);
        }
      }
    }

    setEvalStatus("success");
    onUpdate?.(api);
  }, [api, expression, rangedValueExpression, clear, colors, currentTheme, onUpdate]);

  return { evalStatus };
}
